import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function NewUsersChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(""); // Para debug: "firebase", "estimated", "fallback"

  // =========================================================
  // Helpers
  // =========================================================
  function monthKeyToLabel(monthKey) {
    // monthKey: "YYYY-MM"
    if (!monthKey || typeof monthKey !== "string") return "";
    const [y, m] = monthKey.split("-");
    const year = Number(y);
    const month = Number(m);

    if (!year || !month) return monthKey;

    const d = new Date(year, month - 1, 1);
    const short = d.toLocaleDateString("pt-BR", { month: "short" }); // ex: "nov."
    const monthName = short.replace(".", "");
    const cap = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    // ex: "Nov/25" (fica único por ano)
    const yy = String(year).slice(-2);
    return `${cap}/${yy}`;
  }

  useEffect(() => {
    async function loadUsers() {
      try {
        console.log("🔄 NewUsersChart: Carregando dados do Firebase...");

        const snap = await getDocs(collection(db, "users"));
        console.log(`📊 Total de documentos: ${snap.size}`);

        // Objeto para agrupar por mês-ano
        const monthlyCount = {};
        let usersWithDate = 0;

        snap.forEach((doc) => {
          const userData = doc.data();

          // Tentar encontrar a data de criação
          let createdAt = null;

          // Verificar diferentes formatos possíveis
          if (userData.createdAt) {
            // Timestamp Firestore
            if (userData.createdAt.toDate && typeof userData.createdAt.toDate === "function") {
              createdAt = userData.createdAt.toDate();
            }
            // string
            else if (typeof userData.createdAt === "string") {
              createdAt = new Date(userData.createdAt);
            }
            // Date
            else if (userData.createdAt instanceof Date) {
              createdAt = userData.createdAt;
            }
            // number
            else if (typeof userData.createdAt === "number") {
              createdAt = new Date(userData.createdAt);
            }
            // { seconds: ... }
            else if (userData.createdAt.seconds) {
              createdAt = new Date(userData.createdAt.seconds * 1000);
            }
          }

          // Fallbacks de campo (se você tiver variações)
          if (!createdAt && userData.created_at) {
            if (userData.created_at.toDate) createdAt = userData.created_at.toDate();
            else if (typeof userData.created_at === "string") createdAt = new Date(userData.created_at);
            else if (userData.created_at?.seconds) createdAt = new Date(userData.created_at.seconds * 1000);
          }

          if (!createdAt && userData.signupDate) {
            if (userData.signupDate.toDate) createdAt = userData.signupDate.toDate();
            else if (typeof userData.signupDate === "string") createdAt = new Date(userData.signupDate);
            else if (userData.signupDate?.seconds) createdAt = new Date(userData.signupDate.seconds * 1000);
          }

          if (!createdAt && userData.dateCreated) {
            if (userData.dateCreated.toDate) createdAt = userData.dateCreated.toDate();
            else if (typeof userData.dateCreated === "string") createdAt = new Date(userData.dateCreated);
            else if (userData.dateCreated?.seconds) createdAt = new Date(userData.dateCreated.seconds * 1000);
          }

          if (createdAt && !isNaN(createdAt.getTime())) {
            usersWithDate++;
            const year = createdAt.getFullYear();
            const month = createdAt.getMonth() + 1;
            const key = `${year}-${String(month).padStart(2, "0")}`;

            monthlyCount[key] = (monthlyCount[key] || 0) + 1;
          } else {
            console.warn(`⚠️ Usuário ${doc.id} sem data válida:`, userData);
          }
        });

        console.log(`✅ Usuários com data válida: ${usersWithDate}/${snap.size}`);
        console.log("📈 Contagem mensal:", monthlyCount);

        // Converter para array e ordenar
        let formattedData = Object.entries(monthlyCount)
          .map(([month, users]) => ({
            month, // "YYYY-MM"
            monthLabel: monthKeyToLabel(month), // ✅ SEMPRE EXISTE (corrige o eixo X)
            users: Number(users) || 0,
          }))
          .sort((a, b) => a.month.localeCompare(b.month));

        // Se tiver dados reais
        if (formattedData.length > 0) {
          console.log("🎯 Usando dados REAIS do Firebase");
          setSource("firebase");
          setData(formattedData);
        }
        // Se não tiver dados temporais, criar distribuição estimada
        else {
          console.log("🔄 Nenhum dado temporal, criando distribuição mensal...");
          setSource("estimated");

          const totalUsers = snap.size;
          const currentDate = new Date();
          const estimatedData = [];

          // Distribuir usuários nos últimos 6 meses
          const monthsToShow = 6;
          const baseUsersPerMonth = Math.floor(totalUsers / monthsToShow);
          const remainder = totalUsers % monthsToShow;

          for (let i = monthsToShow - 1; i >= 0; i--) {
            const date = new Date();
            date.setMonth(currentDate.getMonth() - i);

            const year = date.getFullYear();
            const month = date.getMonth() + 1;

            const monthKey = `${year}-${String(month).padStart(2, "0")}`;

            // Distribuição com variação leve
            let usersThisMonth = baseUsersPerMonth;
            if (i === monthsToShow - 1) usersThisMonth += remainder;

            const variation = Math.floor(usersThisMonth * 0.3 * (Math.random() - 0.5));
            usersThisMonth = Math.max(1, usersThisMonth + variation);

            estimatedData.push({
              month: monthKey,
              monthLabel: monthKeyToLabel(monthKey), // ✅ padronizado
              users: usersThisMonth,
            });
          }

          setData(estimatedData);
        }
      } catch (error) {
        console.error("❌ Erro ao carregar dados:", error);
        console.log("🔄 Usando dados de exemplo como fallback");
        setSource("fallback");

        // Dados de fallback
        const fallbackData = [
          { month: "2025-07", monthLabel: monthKeyToLabel("2025-07"), users: 5 },
          { month: "2025-08", monthLabel: monthKeyToLabel("2025-08"), users: 8 },
          { month: "2025-09", monthLabel: monthKeyToLabel("2025-09"), users: 12 },
          { month: "2025-10", monthLabel: monthKeyToLabel("2025-10"), users: 7 },
          { month: "2025-11", monthLabel: monthKeyToLabel("2025-11"), users: 15 },
          { month: "2025-12", monthLabel: monthKeyToLabel("2025-12"), users: 11 },
        ];

        setData(fallbackData);
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, []);

  if (loading) {
    return (
      <div className="chartBox" style={{ minHeight: "450px" }}>
        <h3>Novos usuários por mês 📈</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  return (
    <div
      className="chartBox"
      style={{
        minHeight: "450px",
        background: "white",
        position: "relative",
      }}
    >
      <h3>Novos usuários por mês 📈</h3>

      {/* INFO SOURCE */}
      <div
        style={{
          backgroundColor:
            source === "firebase"
              ? "#f0fff4"
              : source === "estimated"
              ? "#fff4f0"
              : "#f0f9ff",
          border: `1px solid ${
            source === "firebase"
              ? "#48bb78"
              : source === "estimated"
              ? "#ed8936"
              : "#4299e1"
          }`,
          borderRadius: "6px",
          padding: "6px 10px",
          marginBottom: "12px",
          fontSize: "11px",
          color: "#666",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <strong>
            {source === "firebase"
              ? "✅ Dados reais"
              : source === "estimated"
              ? "📊 Estimado"
              : "🔄 Dados de exemplo"}
          </strong>
          {source === "estimated" && " (baseado no total de usuários)"}
        </div>
        <div style={{ fontSize: "10px", opacity: 0.8 }}>{data.length} meses</div>
      </div>

      {/* CONTAINER DO GRÁFICO */}
      <div
        style={{
          width: "100%",
          height: "300px",
          position: "relative",
          minHeight: "300px",
          minWidth: "100px",
        }}
      >
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} debounce={1}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
            <XAxis
              dataKey="monthLabel" // ✅ AGORA SEMPRE EXISTE
              stroke="#888"
              fontSize={11}
              tick={{ fill: "#666" }}
            />
            <YAxis
              stroke="#888"
              fontSize={11}
              tick={{ fill: "#666" }}
              allowDecimals={false}
            />
            <Tooltip
              formatter={(value) => [`${value} usuários`, "Novos"]}
              labelFormatter={(label) => `Mês: ${label}`}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #ff4f9a",
                borderRadius: "8px",
                fontSize: "12px",
                padding: "8px",
              }}
            />
            <Line
              type="monotone"
              dataKey="users"
              stroke="#ff4f9a"
              strokeWidth={3}
              dot={{
                fill: "#ff4f9a",
                strokeWidth: 2,
                r: 5,
                stroke: "#ffffff",
              }}
              activeDot={{
                r: 8,
                fill: "#ff4f9a",
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
              name="Novos usuários"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* RODAPÉ */}
      <div
        style={{
          marginTop: "15px",
          paddingTop: "10px",
          borderTop: "1px solid #eee",
          fontSize: "12px",
          color: "#666",
          textAlign: "center",
        }}
      >
        Período:{" "}
        {data.length > 0 ? `${data[0].month} a ${data[data.length - 1].month}` : "Sem dados"} •
        Total: {data.reduce((sum, item) => sum + (Number(item.users) || 0), 0)} novos usuários
      </div>

      {/* DEBUG INFO */}
      <div
        style={{
          fontSize: "10px",
          color: "#999",
          marginTop: "10px",
          padding: "5px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
        }}
      >
        DEBUG: {data.length} meses | Total usuários:{" "}
        {data.reduce((sum, item) => sum + (Number(item.users) || 0), 0)} | Source: {source}
      </div>
    </div>
  );
}

export default NewUsersChart;
